import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import PersonAddIcon from "@material-ui/icons/PersonAdd";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import NewTicketModal from "../../components/NewTicketModal";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    backgroundColor:
      theme.palette.type === "dark" || theme.palette.mode === "dark"
        ? "#1e1e1e"
        : theme.palette.background.default,
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    paddingTop: 10,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    backgroundColor:
      theme.palette.type === "dark" || theme.palette.mode === "dark"
        ? "#1f1f1f"
        : theme.palette.background.paper,
    borderRadius: "18px 0 0 0 !important",
    border: "none",
    boxShadow: "none",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      borderRadius: 0,
    },
  },
  tableScrollArea: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    ...theme.scrollbarStyles,
  },
  actionButton: {
    borderRadius: 999,
    boxShadow: "none",
    textTransform: "none",
    minWidth: 0,
    "&:hover": {
      boxShadow: "none",
    },
  },
  actionButtonCompact: {
    width: 42,
    minWidth: 42,
    height: 42,
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  colAvatar: {
    width: "auto",
    overflow: "visible",
    textOverflow: "clip",
  },
  colName: { width: "auto" },
  colWhatsapp: { width: "auto" },
  colEmail: { width: "auto" },
  colActions: {
    width: "auto",
    whiteSpace: "nowrap",
    overflow: "visible",
    textOverflow: "clip",
  },
}));

const Contacts = () => {
  const classes = useStyles();
  const history = useHistory();
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down("sm"));

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedContactForTicket, setSelectedContactForTicket] = useState(null);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/contacts/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("contact", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleOpenTicketModal = (contact) => {
    if (!contact?.id) return;
    setSelectedContactForTicket(contact);
    setNewTicketModalOpen(true);
  };

  const handleCloseNewTicketModal = () => {
    setNewTicketModalOpen(false);
    setSelectedContactForTicket(null);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleimportContact = async () => {
    try {
      await api.post("/contacts/import");
      history.go(0);
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer className={classes.mainContainer}>
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactModal>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={handleCloseNewTicketModal}
        initialContact={selectedContactForTicket}
      />
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${
                deletingContact.name
              }?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={(e) =>
          deletingContact
            ? handleDeleteContact(deletingContact.id)
            : handleimportContact()
        }
      >
        {deletingContact
          ? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>
      <Paper
        className={classes.mainPaper}
      >
        <MainHeader>
          <Title>{i18n.t("contacts.title")}</Title>
          <MainHeaderButtonsWrapper>
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={`${classes.actionButton} ${isSmallScreen ? classes.actionButtonCompact : ""}`}
              startIcon={isSmallScreen ? undefined : <CloudUploadIcon />}
              aria-label={i18n.t("contacts.buttons.import")}
              onClick={(e) => setConfirmOpen(true)}
            >
              {isSmallScreen ? <CloudUploadIcon /> : i18n.t("contacts.buttons.import")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={`${classes.actionButton} ${isSmallScreen ? classes.actionButtonCompact : ""}`}
              startIcon={isSmallScreen ? undefined : <PersonAddIcon />}
              aria-label={i18n.t("contacts.buttons.add")}
              onClick={handleOpenContactModal}
            >
              {isSmallScreen ? <PersonAddIcon /> : i18n.t("contacts.buttons.add")}
            </Button>
          </MainHeaderButtonsWrapper>
        </MainHeader>
        <div className={classes.tableScrollArea} onScroll={handleScroll}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" className={classes.colAvatar} />
                <TableCell className={classes.colName}>{i18n.t("contacts.table.name")}</TableCell>
                <TableCell className={classes.colWhatsapp}>
                  {i18n.t("contacts.table.whatsapp")}
                </TableCell>
                <TableCell className={classes.colEmail}>
                  {i18n.t("contacts.table.email")}
                </TableCell>
                <TableCell className={classes.colActions}>
                  {i18n.t("contacts.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <>
                {contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell className={classes.colAvatar} style={{ paddingRight: 0 }}>
                      {<Avatar src={contact.profilePicUrl} />}
                    </TableCell>
                    <TableCell className={classes.colName}>{contact.name}</TableCell>
                    <TableCell className={classes.colWhatsapp}>{contact.number}</TableCell>
                    <TableCell className={classes.colEmail}>{contact.email}</TableCell>
                    <TableCell className={classes.colActions}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenTicketModal(contact)}
                      >
                        <WhatsAppIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => hadleEditContact(contact.id)}
                      >
                        <EditIcon />
                      </IconButton>
                      <Can
                        role={user.profile}
                        perform="contacts-page:deleteContact"
                        yes={() => (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setConfirmOpen(true);
                              setDeletingContact(contact);
                            }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        )}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton avatar columns={3} />}
              </>
            </TableBody>
          </Table>
        </div>
      </Paper>
    </MainContainer>
  );
};

export default Contacts;
